<script lang="ts" setup>
import { ref, reactive, onMounted, watch } from "vue";

import { getKZTitle, getKZValue, getKZValueDisplay } from "@/DataHelper";
import {
  AufgabeStatusZuordnung,
  getAlternateColorChart,
  setQuickviewSelectionCallBack,
} from "../ChartTypesAndFunctions";
import DashboardChartsSmallButtonBar from "@/components/dashboard/Charts/SmallButtonBar.vue";
import DashboardChartsZuordnungenMenu from "@/components/dashboard/Charts/ZuordnungenMenu.vue";

import {
  initChartBase,
  ChartBaseProperties,
  ChartBaseUiData,
  getKZAufgabeDefiniton,
  showDialogChart,
  ChartBaseParameters,
  refreshChartData,
} from "./ChartBaseStatic";
import { begriffBezeichnung } from "@/local";

declare interface InfoData {
  header: string;
  title: string;
  titleText: string;
  subtitel: string;
  subtitelText: string;
  text: string;
  aic: number;
  show: boolean;

  selected: boolean;
}

interface QuickInfoProps extends ChartBaseProperties {
  persoenlich?: boolean;
}
interface ChartUiData extends ChartBaseUiData {
  noData: boolean;
  showLoading: boolean;
  showLength: boolean;
  showZeitbereich?: boolean;
  alert: boolean;
  titleTime: string;
  showTitleTime: boolean;
  infoText: string;
  showInfo: boolean;
  showZuordnung: boolean;
  pers: boolean;
  zuordnungen: AufgabeStatusZuordnung[];
}
const props = defineProps<QuickInfoProps>();
const emit = defineEmits([
  "refresh",
  "info",
  "aicsLoaded",
  "vectorChanged",
  "buildFilter",
]);

const { ui, params } = initChartBase<ChartUiData, ChartBaseParameters>(
  props,
  {
    noData: false,
    showLoading: true,
    showLength: true,
    tooltip: [],
    alert: false,
    titleTime: "",
    showTitleTime: false,
    infoText: "",
    showInfo: false,
    showZuordnung: true,
    pers: false,
  },
  {},
);

let vector: number[] = [];
let responsiveOptions: any;

let infoDataTotal: InfoData[] = [];
let infoData = ref<InfoData[]>([]);
let showInfo: boolean = false;
let subFrame!: any;

const uiData = reactive<ChartUiData>(ui);
onMounted(async () => {
  setQuickviewSelectionCallBack((v) => onCheckAll(v));
  refresh();
});

let lastLength = 0;
/*watch(
  () => props.QvAICs,
  () => {
    if (props.QvAICs?.length !== lastLength) {
      lastLength = props.QvAICs?.length ?? 0;
      refresh();
      refreshAics();
    }
  }
); */
function onCheckAll(val: boolean) {
  infoData.value.forEach((i) => {
    i.selected = val;
  });
  selectionChanged();
}
async function refresh() {
  try {
    uiData.showLoading = true;
    const data = await refreshChartData(uiData, props, params);
    dataLoaded(data);
  } catch (error) {
    console.error(error);
    uiData.showLoading = false;
  }
}
function dataLoaded(data: QueryResponse) {
  try {
    emit("buildFilter", data);
    const aics: number[] = [];
    infoData.value = [];
    for (let i = 0; i < data.data.length; i++) {
      const row = data.data[i];
      const header = getKZValueDisplay(row, "header", data.columns); // TODO Kennung
      const title = getKZValueDisplay(row, "titel", data.columns);
      let titleText = "";
      if (title) {
        titleText = getKZTitle(row, "titel", data.columns);
      }
      const subtitel = getKZValueDisplay(row, "subtitel", data.columns);
      let subtitelText = "";
      if (subtitel) {
        subtitelText = getKZTitle(row, "subtitel", data.columns);
      }
      const text = getKZValueDisplay(row, "text", data.columns);
      let aic = getKZValue(row, "modellAic", data.columns);
      const show = false;
      if (!aic || aic === undefined) {
        if (row.aic_Bew_pool) {
          aic = row.aic_Bew_pool;
        } else {
          aic = row.aic_Stamm;
        }
      }
      aics.push(aic);
      const item: InfoData = {
        header,
        title,
        titleText,
        subtitel,
        subtitelText,
        text,
        aic,
        show,
        selected: false,
      };
      infoDataTotal.push(item);
      if ((data?.data?.length ?? 0) === 0) {
        uiData.noData = true;
        return;
      }
    }
    const headerData = data.header;
    if (headerData.fomDetail) {
      subFrame = headerData.fomDetail;
    }
    emit("aicsLoaded", ...aics);
  } finally {
    if (props.QvAICs && props.QvAICs.length > 0) {
      refreshAics();
    } else {
      infoData.value = infoDataTotal;
    }
    uiData.showLoading = false;
  }
}
function refreshAics() {
  infoData.value = infoDataTotal.filter(
    (i) => props.QvAICs!.indexOf(i.aic) >= 0,
  );
}
function logData() {
  if (uiData.showLength) {
    uiData.alert = !uiData.alert;
  }
  console.log(params.lastQueryResponse);
}
function selectionChanged() {
  vector = infoData.value.filter((i) => i.selected).map((i) => i.aic);
  emit("vectorChanged", vector);
}
</script>
<template>
  <v-card :loading="uiData.showLoading" class="pa-0" flat min-width="100%">
    <v-card-text class="pa-0">
      <DashboardChartsSmallButtonBar
        @refresh="refresh"
        @info="logData"
        :showInfo="uiData.showInfo"
      />
      <v-list
        v-if="!uiData.showLoading && !uiData.noData"
        lines="three"
        class="pa-0"
        tile
        style="overflow: hidden"
      >
        <v-list-item
          v-for="(item, ix) in infoData"
          :key="'info' + ix"
          :style="{
            padding: '0 0 0 0',
            background: getAlternateColorChart(ix),
            height: '200px',
            width: '100%',
          }"
        >
          <div
            class="align-self-start"
            style="
              background: transparent;
              width: 90%;
              display: block;
              padding: 0;
            "
            @click="
              showDialogChart(
                uiData,
                props,
                params.zeitbereich!,
                subFrame,
                item.aic,
              )
            "
          >
            <v-list-item-title class="font-weight-light">
              {{ item.header }}
            </v-list-item-title>

            <v-list-item-subtitle v-if="item.title">
              {{ item.titleText }}: {{ item.title }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="item.subtitel"
              >{{ item.subtitelText }}:
              {{ item.subtitel }}</v-list-item-subtitle
            >
            <v-divider class="py-2"></v-divider>
            <v-list-item-subtitle
              class="font-weight-light grey--text pa-0"
              style="text-align: left"
              >{{ item.text }}</v-list-item-subtitle
            >
          </div>
          <template v-slot:append>
            <v-list-item-action
              style="width: 50px"
              class="d-flex align-self-start"
            >
              <DashboardChartsZuordnungenMenu
                v-if="
                  uiData.zuordnungen &&
                  uiData.zuordnungen.length > 0 &&
                  uiData.showZuordnung
                "
                :zuordnungen="uiData.zuordnungen"
                :aic="item.aic"
                :persoenlich="props.persoenlich"
                :showButtons="true"
                style="min-width: 20px; max-width: 20px; flex-shrink: 0"
              />

              <v-checkbox
                class="pt-5"
                v-model="item.selected"
                @update:modelValue="selectionChanged"
              />
            </v-list-item-action>
          </template>
        </v-list-item>
      </v-list>
      <div v-if="uiData.noData">
        {{
          begriffBezeichnung("Web_keineDaten_gefunden", "keine Daten gefunden")
        }}
      </div>
    </v-card-text>
  </v-card>
</template>
<style scoped>
.v-list--three-line .v-list-item .v-list-item__avatar,
.v-list--three-line .v-list-item .v-list-item__action,
.v-list-item--three-line .v-list-item__avatar,
.v-list-item--three-line .v-list-item__action {
  margin-top: 0px;
}
</style>
